import {
  TABLE_SORTER_TOOLTIP,
  LIST_CREATE_BUTTON_STYLE,
  LIST_SURFACE_STYLE,
} from '../../../shared/ui/list-styles.js';
import { QuietEmpty, QuietListSkeleton } from '../../../shared/ui/quiet-states.js';
// Platform admin LLM monitoring screen (archetype: DataView + summary cards).
//
// Period-bounded aggregations over Message.costRub/tokens: KPI cards for the
// window, then three views — by workspace (null group = unassigned historic
// traffic), by gateway profile, and top conversations by spend. Numbers are
// monospace right-aligned (catalog §6.3); money is a Decimal-string from the
// API and is formatted with Intl on the client.
import { useMemo, useState, type ReactElement } from 'react';
import { Button, Card, Col, Row, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table/interface';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { OverviewStatCard } from '../components/OverviewStatCard.js';
import {
  LlmPeriodFilter,
  defaultLlmPeriodState,
  type LlmPeriodState,
} from '../components/LlmPeriodFilter.js';
import {
  useAdminLlmByProfile,
  useAdminLlmByWorkspace,
  useAdminLlmSummary,
  useAdminLlmTopConversations,
} from '../hooks/useAdminLlm.js';
import { formatAdminCount, formatAdminSpend, formatDateTime } from '../lib/format.js';
import type {
  LlmByProfileRow,
  LlmByWorkspaceRow,
  LlmTopConversationRow,
} from '../api/admin.client.js';

const { Text } = Typography;

type LlmTab = 'workspaces' | 'profiles' | 'top';

const KNOWN_PROFILES: readonly string[] = ['neuraldeep', 'openrouter', 'govard', 'opencode'];

function profileLabel(profile: string, t: (key: string) => string): string {
  return KNOWN_PROFILES.includes(profile) ? t(`crm.admin.llm.profile.${profile}`) : profile;
}

export function AdminLlmPage(): ReactElement {
  const { t } = useTranslation();
  const [periodState, setPeriodState] = useState<LlmPeriodState>(defaultLlmPeriodState);
  const [tab, setTab] = useState<LlmTab>('workspaces');

  const period = periodState.period;
  const summaryQuery = useAdminLlmSummary(period);
  const byWorkspaceQuery = useAdminLlmByWorkspace(period);
  const byProfileQuery = useAdminLlmByProfile(period);
  const topConversationsQuery = useAdminLlmTopConversations(period);

  const anyLoading =
    summaryQuery.isLoading || byWorkspaceQuery.isLoading || byProfileQuery.isLoading;

  function refetchAll(): void {
    void summaryQuery.refetch();
    void byWorkspaceQuery.refetch();
    void byProfileQuery.refetch();
    void topConversationsQuery.refetch();
  }

  const byWorkspaceColumns = useMemo<Array<ColumnType<LlmByWorkspaceRow>>>(
    () => [
      {
        title: t('crm.admin.llm.col_workspace'),
        key: 'workspace',
        render: (_: unknown, row) =>
          row.workspaceId === null ? (
            <Tooltip title={t('crm.admin.llm.unassigned_hint')}>
              <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {t('crm.admin.llm.unassigned')}
              </Tag>
            </Tooltip>
          ) : (
            <div className="flex min-w-0 flex-col gap-0.5">
              <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }}>
                {row.workspaceName ?? '—'}
              </Text>
              <Text className="font-mono" type="secondary" style={{ fontSize: 11 }} ellipsis>
                {row.workspaceSlug ?? row.workspaceId}
              </Text>
            </div>
          ),
      },
      {
        title: t('crm.admin.llm.col_messages'),
        dataIndex: 'messages',
        key: 'messages',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_tokens'),
        dataIndex: 'tokens',
        key: 'tokens',
        width: 130,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_cost'),
        dataIndex: 'costRub',
        key: 'costRub',
        width: 140,
        align: 'right',
        render: (value: string) => (
          <Text className="font-semibold font-mono" style={{ fontSize: 12 }}>
            {formatAdminSpend(value)}
          </Text>
        ),
      },
    ],
    [t],
  );

  const byProfileColumns = useMemo<Array<ColumnType<LlmByProfileRow>>>(
    () => [
      {
        title: t('crm.admin.llm.col_profile'),
        dataIndex: 'profile',
        key: 'profile',
        render: (value: string) => (
          <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{profileLabel(value, t)}</Tag>
        ),
      },
      {
        title: t('crm.admin.llm.col_conversations'),
        dataIndex: 'conversations',
        key: 'conversations',
        width: 130,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_messages'),
        dataIndex: 'messages',
        key: 'messages',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_tokens'),
        dataIndex: 'tokens',
        key: 'tokens',
        width: 130,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_cost'),
        dataIndex: 'costRub',
        key: 'costRub',
        width: 140,
        align: 'right',
        render: (value: string) => (
          <Text className="font-semibold font-mono" style={{ fontSize: 12 }}>
            {formatAdminSpend(value)}
          </Text>
        ),
      },
    ],
    [t],
  );

  const topColumns = useMemo<Array<ColumnType<LlmTopConversationRow>>>(
    () => [
      {
        title: t('crm.admin.llm.col_conversation'),
        key: 'conversation',
        render: (_: unknown, row) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text strong style={{ color: 'var(--ant-color-text)', fontSize: 13 }}>
              {row.title ?? t('crm.admin.llm.untitled')}
            </Text>
            <Text className="font-mono" type="secondary" style={{ fontSize: 11 }} ellipsis>
              {row.workspaceName ?? row.workspaceId ?? t('crm.admin.llm.unassigned')}
            </Text>
          </div>
        ),
      },
      {
        title: t('crm.admin.llm.col_user'),
        dataIndex: 'userEmail',
        key: 'userEmail',
        width: 190,
        ellipsis: true,
        render: (value: string | null) =>
          value === null ? (
            <Text disabled style={{ fontSize: 12 }}>
              —
            </Text>
          ) : (
            <Text className="font-mono" style={{ fontSize: 12 }} ellipsis>
              {value}
            </Text>
          ),
      },
      {
        title: t('crm.admin.llm.col_messages'),
        dataIndex: 'messages',
        key: 'messages',
        width: 110,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_tokens'),
        dataIndex: 'tokens',
        key: 'tokens',
        width: 130,
        align: 'right',
        render: (value: number) => (
          <Text className="font-mono" style={{ fontSize: 12 }}>
            {formatAdminCount(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_cost'),
        dataIndex: 'costRub',
        key: 'costRub',
        width: 140,
        align: 'right',
        render: (value: string) => (
          <Text className="font-semibold font-mono" style={{ fontSize: 12 }}>
            {formatAdminSpend(value)}
          </Text>
        ),
      },
      {
        title: t('crm.admin.llm.col_last_message'),
        dataIndex: 'lastMessageAt',
        key: 'lastMessageAt',
        width: 150,
        render: (value: string | null) => (
          <Tooltip title={value === null ? undefined : formatDateTime(value)}>
            <Text style={{ fontSize: 12, color: 'var(--ant-color-text-secondary)' }}>
              {formatDateTime(value)}
            </Text>
          </Tooltip>
        ),
      },
    ],
    [t],
  );

  if (summaryQuery.isError) {
    return (
      <ErrorState
        title={t('crm.admin.llm.load_error')}
        error={summaryQuery.error}
        onRetry={refetchAll}
      />
    );
  }

  const summary = summaryQuery.data;

  return (
    <div className="animate-fade-in flex flex-col">
      <PageHeader
        title={t('crm.admin.llm.title')}
        subtitle={t('crm.admin.llm.subtitle', {
          from: formatDateTime(period.from),
          to: formatDateTime(period.to),
        })}
        actions={
          <>
            <LlmPeriodFilter value={periodState} onChange={setPeriodState} />
            <Button
              icon={<ReloadOutlined />}
              onClick={refetchAll}
              loading={
                summaryQuery.isFetching ||
                byWorkspaceQuery.isFetching ||
                byProfileQuery.isFetching ||
                topConversationsQuery.isFetching
              }
              aria-label={t('crm.admin.llm.reload')}
              style={LIST_CREATE_BUTTON_STYLE}
            >
              {t('crm.admin.llm.reload')}
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3">
        {anyLoading && summary === undefined ? (
          <div style={LIST_SURFACE_STYLE}>
            <QuietListSkeleton rows={3} />
          </div>
        ) : (
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <OverviewStatCard
                label={t('crm.admin.llm.kpi_spend')}
                value={formatAdminSpend(summary?.costRub)}
                testId="admin-llm-kpi-spend"
              />
            </Col>
            <Col xs={12} md={6}>
              <OverviewStatCard
                label={t('crm.admin.llm.kpi_tokens')}
                value={formatAdminCount(summary?.tokens)}
                testId="admin-llm-kpi-tokens"
              />
            </Col>
            <Col xs={12} md={6}>
              <OverviewStatCard
                label={t('crm.admin.llm.kpi_messages')}
                value={formatAdminCount(summary?.messages)}
                testId="admin-llm-kpi-messages"
              />
            </Col>
          </Row>
        )}

        <Card size="small" styles={{ body: { padding: 0 } }} data-testid="admin-llm-views">
          <Tabs
            activeKey={tab}
            onChange={(activeKey) => setTab(activeKey as LlmTab)}
            items={[
              {
                key: 'workspaces',
                label: t('crm.admin.llm.tab_by_workspace'),
                children: byWorkspaceQuery.isError ? (
                  <div className="p-6">
                    <ErrorState
                      title={t('crm.admin.llm.load_error')}
                      error={byWorkspaceQuery.error}
                      onRetry={() => void byWorkspaceQuery.refetch()}
                    />
                  </div>
                ) : (
                  <Table<LlmByWorkspaceRow>
                    showSorterTooltip={TABLE_SORTER_TOOLTIP}
                    size="small"
                    rowKey={(row) => row.workspaceId ?? 'unassigned'}
                    columns={byWorkspaceColumns}
                    dataSource={byWorkspaceQuery.data ?? []}
                    loading={byWorkspaceQuery.isLoading}
                    pagination={false}
                    locale={{
                      emptyText: <QuietEmpty text={t('crm.admin.llm.empty')} />,
                    }}
                  />
                ),
              },
              {
                key: 'profiles',
                label: t('crm.admin.llm.tab_by_profile'),
                children: byProfileQuery.isError ? (
                  <div className="p-6">
                    <ErrorState
                      title={t('crm.admin.llm.load_error')}
                      error={byProfileQuery.error}
                      onRetry={() => void byProfileQuery.refetch()}
                    />
                  </div>
                ) : (
                  <Table<LlmByProfileRow>
                    showSorterTooltip={TABLE_SORTER_TOOLTIP}
                    size="small"
                    rowKey="profile"
                    columns={byProfileColumns}
                    dataSource={byProfileQuery.data ?? []}
                    loading={byProfileQuery.isLoading}
                    pagination={false}
                    locale={{
                      emptyText: <QuietEmpty text={t('crm.admin.llm.empty')} />,
                    }}
                  />
                ),
              },
              {
                key: 'top',
                label: t('crm.admin.llm.tab_top_conversations'),
                children: topConversationsQuery.isError ? (
                  <div className="p-6">
                    <ErrorState
                      title={t('crm.admin.llm.load_error')}
                      error={topConversationsQuery.error}
                      onRetry={() => void topConversationsQuery.refetch()}
                    />
                  </div>
                ) : (
                  <Table<LlmTopConversationRow>
                    showSorterTooltip={TABLE_SORTER_TOOLTIP}
                    size="small"
                    rowKey="conversationId"
                    columns={topColumns}
                    dataSource={topConversationsQuery.data ?? []}
                    loading={topConversationsQuery.isLoading}
                    pagination={false}
                    locale={{
                      emptyText: <QuietEmpty text={t('crm.admin.llm.empty')} />,
                    }}
                  />
                ),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
